import { LayoutDashboard, Truck } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"

import { BreadcrumbNav } from "@/components/breadcrumb-nav"
import { LoadingState } from "@/components/loading-state"
import { PageHeader } from "@/components/page-header"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { CooperativeDeliveriesTable } from "@/features/cooperatives/components/cooperative-deliveries-table"
import { CooperativeInfoCard } from "@/features/cooperatives/components/cooperative-info-card"
import { CooperativeKpis } from "@/features/cooperatives/components/cooperative-kpis"
import { CooperativeOrdersTable } from "@/features/cooperatives/components/cooperative-orders-table"
import { useCooperative } from "@/features/cooperatives/hooks/use-cooperatives"
import { paths } from "@/routes/paths"

export function CooperativeDetailPage() {
  const { cooperativeId = "" } = useParams<{ cooperativeId: string }>()
  const { t } = useTranslation(["cooperatives"])
  const { data: cooperative, isLoading } = useCooperative(cooperativeId)

  if (isLoading || !cooperative) {
    return <LoadingState />
  }

  const deliveriesCount = cooperative.deliveries?.length ?? 0

  return (
    <div className="flex flex-col gap-6">
      <BreadcrumbNav
        crumbs={[
          { label: t("title"), to: paths.cooperatives },
          { label: cooperative.name ?? `#${cooperative.id}` },
        ]}
      />

      <PageHeader
        title={cooperative.name ?? `#${cooperative.id}`}
        description={cooperative.region ?? undefined}
      />

      <Tabs defaultValue="overview">
        <TabsList variant="line" className="justify-start gap-6">
          <TabsTrigger value="overview" className="gap-2">
            <LayoutDashboard className="size-4" />
            {t("tabs.overview")}
          </TabsTrigger>
          <TabsTrigger value="deliveries" className="gap-2">
            <Truck className="size-4" />
            {t("tabs.deliveries")} ({deliveriesCount})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6 flex flex-col gap-6">
          <CooperativeKpis groupId={cooperative.id} />
          <CooperativeInfoCard cooperative={cooperative} />
          <CooperativeOrdersTable orders={cooperative.orders} />
        </TabsContent>

        <TabsContent value="deliveries" className="mt-6">
          <CooperativeDeliveriesTable deliveries={cooperative.deliveries} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
